<template>
  <IndicatorCard
    :loading="loading"
    :has-data="equips.length > 0"
    :is-result="isResult"
    :no-data-message="$t('Nenhum equipamento com chamado recorrente encontrado')"
    :title="title"
    :description="indicator.description"
    :excel-endpoint="indicator.excelEndpoint"
    :hide-excel="!isResult"
    :excel-loading="excelLoading"
    @export-excel="requestExcel"
  >
    <template #date-range>
      <DateRangePicker
        :start-date="startDate"
        :end-date="endDate"
        :disabled="isResult"
        @change="onDateRangeChange"
      />
    </template>

    <v-row class="ma-0" justify="center">
      <v-col cols="12">
        <BaseEChart :option="chartOption" :loading="loading" height="320px" />

        <div class="wrapper">
          <v-data-table
            :loading="loading"
            :loading-text="$t('Carregando...')"
            :no-data-text="$t('Nenhum dado encontrado')"
            :headers="headers"
            :items="equips"
            class="main-table"
            hide-default-footer
            hide-default-header
            :header-props="headerProps"
            :items-per-page="filter.pageSize"
            :mobile-breakpoint="0"
          >
            <template #header="{ props, on }">
              <thead>
                <tr>
                  <th
                    class="text-center"
                    v-for="header in props.headers"
                    :key="header.value"
                  >
                    <div>
                      <v-hover v-slot="{ hover }">
                        <div>
                          <span>{{ $t(header.text) }}</span>
                          <v-icon
                            v-if="hover && header.value !== 'actions'"
                            color="white"
                            class="mx-2"
                            @click="on.sort(header.value)"
                          >
                            {{
                              props.options.sortBy.includes(header.value)
                                ? 'mdi-chevron-down'
                                : 'mdi-chevron-up'
                            }}
                          </v-icon>
                        </div>
                      </v-hover>
                    </div>
                  </th>
                </tr>
              </thead>
            </template>

            <template v-slot:[`item.totalCalls`]="{ item }">
              <strong>{{ item.totalCalls }}</strong>
            </template>
          </v-data-table>

          <div class="pagination" v-if="!filterProp.data">
            <div class="item-pagination block-two">
              <v-pagination
                class="pagination-item"
                v-model="page"
                :length="totalPages"
                :total-visible="9"
              ></v-pagination>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <template #review>
      <v-row class="ma-0">
        <v-col cols="12" v-if="isResult && review">
          <strong>{{ $t('Análise crítica') }}</strong>
          <p v-html="review"></p>
        </v-col>
        <v-col cols="12" v-else-if="!isResult">
          <tiptap-vuetify
            v-model="filter.review"
            :extensions="extensions"
            :label="$t('Análise crítica')"
            :placeholder="$t('Digite sua análise crítica para o indicador abaixo')"
            :card-props="{ flat: true, outlined: true, rounded: true }"
            class="primary-input"
            outlined
            dense
            hide-details="auto"
          ></tiptap-vuetify>
        </v-col>
      </v-row>
    </template>
  </IndicatorCard>
</template>

<script>
import { getIndicatorById } from '../../../../../indicators/indicatorRegistry'
import ApiService from '../../../../../../../../services/ApiService'
import IndicatorCard from '../../shared/IndicatorCard.vue'
import BaseEChart from '../../shared/BaseEChart.vue'
import DateRangePicker from '../../shared/DateRangePicker.vue'
import { buildHorizontalBarOption } from '../../shared/chartConfigs'
import moment from 'moment'
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
} from 'tiptap-vuetify'

export default {
  name: 'RecurringCalls',

  components: {
    IndicatorCard,
    BaseEChart,
    DateRangePicker,
    TiptapVuetify,
  },

  props: {
    title: { type: String, default: '' },
    apiEndpointOverride: { type: String, default: '' },
    snapshotData: { type: Object, default: null },
    filterProp: Object,
    institution: Object,
    unidades: Array,
    isResult: { type: Boolean, default: false },
    review: String,
  },

  data: () => ({
    excelLoading: false,
    loading: true,
    extensions: [
      History, Blockquote, Link, Underline, Strike, Italic, ListItem,
      BulletList, OrderedList,
      [Heading, { options: { levels: [1, 2, 3] } }],
      Bold, Code, HorizontalRule, Paragraph, HardBreak,
    ],
    filter: {
      page: 1,
      pageSize: 10,
      review: '',
    },
    apiService: new ApiService(),
    startDate: null,
    endDate: null,
    equips: [],
    equipmentNames: [],
    equipmentIdentifications: [],
    totalCalls: [],
    page: 1,
    totalPages: 1,
    headerProps: { sortByText: 'Ordenar por' },
    headers: [
      { text: 'Equipamento', align: 'left', value: 'equipmentName' },
      { text: 'Fabricante', align: 'left', value: 'manufacturer' },
      { text: 'Modelo', align: 'left', value: 'model' },
      { text: 'Identificação', align: 'center', value: 'identification' },
      { text: 'Unidade', align: 'left', value: 'unitName' },
      { text: 'Setor', align: 'left', value: 'departmentName' },
      { text: 'Chamados', align: 'center', value: 'totalCalls' },
    ],
  }),

  computed: {
    indicator() {
      return getIndicatorById('recurring_calls') || {}
    },

    chartOption() {
      const identifications = this.equipmentIdentifications
      const option = buildHorizontalBarOption({
        categories: this.equipmentNames,
        series: [
          {
            name: this.$t('Chamados Corretivos'),
            data: this.totalCalls,
            color: '#0F3565',
          },
        ],
        showLegend: false,
      })
      option.series[0].label = {
        ...option.series[0].label,
        position: 'insideLeft',
        color: '#ffffff',
        formatter: (params) => {
          const id = identifications[params.dataIndex]
          return id ? id : params.value > 0 ? String(params.value) : ''
        },
      }
      return option
    },
  },

  async created() {
    const lastMonthStart = moment()
      .add(-1, 'month')
      .clone()
      .startOf('month')
      .format('YYYY-MM-DD')
    const lastMonthEnd = moment()
      .add(-1, 'month')
      .clone()
      .endOf('month')
      .format('YYYY-MM-DD')

    this.filter.institutionId = this.institution.id
    this.filter.unidadeIds = this.unidades
    this.filter.startDate = lastMonthStart
    this.filter.endDate = lastMonthEnd
    this.startDate = lastMonthStart
    this.endDate = lastMonthEnd

    if (this.filterProp) {
      this.filter = this.filterProp
      this.startDate = moment(this.filter.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')
      this.endDate = moment(this.filter.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    }

    this.list()
  },

  methods: {
    requestExcel() {
      if (!this.indicator.excelEndpoint) return
      this.excelLoading = true
      const filename = `${this.indicator.legacyKey}.xlsx`
      this.apiService
        .post(this.indicator.excelEndpoint, { ...this.filter, pageSize: 999999 })
        .then((response) => {
          const data = `data:application/octet-stream;base64,${response.content}`
          const link = document.createElement('a')
          link.href = data
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .finally(() => {
          this.excelLoading = false
        })
    },

    setDates() {
      this.filter.startDate = moment(this.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')
      this.filter.endDate = moment(this.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    },

    onDateRangeChange({ startDate, endDate }) {
      this.startDate = startDate
      this.endDate = endDate
      this.filterProp.data = null
      this.list()
    },

    setInfos() {
      const model = {
        chart: getIndicatorById('recurring_calls').legacyKey,
        ...this.filter,
        data: null,
      }
      this.$emit('setInfos', model)
    },

    extractChartData(response) {
      this.totalPages = response.content.pager.totalPages
      this.equips = response.content.objects
      this.equipmentNames = []
      this.equipmentIdentifications = []
      this.totalCalls = []

      const chartItems = response.content.objects.slice(0, 7)
      chartItems.forEach((obj) => {
        this.equipmentNames.push(obj.equipmentName)
        this.equipmentIdentifications.push(obj.identification || '')
        this.totalCalls.push(obj.totalCalls)
      })
    },

    async list() {
      this.loading = true
      try {
        if (this.filterProp.data) {
          this.extractChartData(JSON.parse(this.filterProp.data))
          return
        }

        if (!this.isResult) this.setDates()
        this.filter.institutionId = this.institution.id
        this.filter.page = this.page
        this.filter.unidadeIds = this.unidades

        const response = await this.apiService.post(
          this.apiEndpointOverride || this.indicator.apiEndpoint,
          this.filter
        )
        this.extractChartData(response)
      } finally {
        this.loading = false
      }
    },
  },

  watch: {
    page() {
      this.filter.page = this.page
      this.list()
    },
    filter: {
      deep: true,
      handler() {
        this.setInfos()
      },
    },
  },
}
</script>
